import { pack } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, UnsupportedNodeError } from './utils'

/**
 * Returns `true` if at least one item in the given array is `true`. Otherwise,
 * returns `false`.
 *
 * @param expressionOrExpressions Either a single expression which returns a
 * list of booleans or a tuple of two expressions, where the first returns a
 * list and the second is applied to each item in the list to retrieve a boolean
 * @example
 * {
 *   every: { '[]': [ false, true, false ] }
 * }
 * // =>
 * false
 * {
 *   every: { '[]': [ true, true, true ] }
 * }
 * // =>
 * true
 * {
 *   every: { '[]': [ false, false, false ] }
 * }
 * // =>
 * false
 *
 * {
 *   every: [{ '[]': [ 1, 2, 3 ] }, { '>': [ { '@$': '$value' }, 2.5 ] }]
 * }
 * // =>
 * false
 * {
 *   every: [{ '[]': [ 1, 2, 3 ] }, { '>': [ { '@$': '$value' }, -25 ] }]
 * }
 * // =>
 * true
 * {
 *   every: [{ '[]': [ 1, 2, 3 ] }, { '>': [ { '@$': '$value' }, 25 ] }]
 * }
 * // =>
 * false
 */
export interface Every<out T = any> {
  /** {@link Every} */
  readonly [Key.Every]: T extends boolean
    ?
        | Expression<readonly boolean[]>
        | [Expression<readonly unknown[]>, Expression<boolean>]
    : never
}
export namespace Every {
  export const isEvery: TypeChecker<Every> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Every>({
      [Key.Every]: CommonArgs.isExpressionOrTwoExpressions as TypeChecker<
        Every[Key.Every]
      >,
    }),
  ).usingAlias('Fn.Every')
  export const assertIsEvery: TypeAsserter<Every> =
    TypeAsserter.Factory.from(isEvery)

  /**
   * @see {@link Node}
   */
  export class Node<T extends boolean = boolean> extends fnNode.Node<
    T,
    Key.Every,
    fnNode.Node<readonly boolean[]>
  > {
    public readonly type = common.value.types.Intrinsic.Boolean

    constructor(arg: Node<T>['arg']) {
      super(Key.Every, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Every>['expressionsInArgs'] = fn =>
    pack(fn[Key.Every])
  export const HELPER: FnHelper<Every> = {
    createNode: (fn, context) => {
      const arg = fn[Key.Every]
      let argNode: expressionNode.Node<readonly boolean[]>
      if (TypeChecker.isReadonlyArray(arg)) {
        // If we are given two arguments, we pass those into a map function
        argNode = expressionNode.from({ '->': arg }, context)
      } else {
        // Otherwise, we assume it is an expression that returns a list of
        // booleans
        argNode = expressionNode.from(arg, context)
      }
      if (argNode instanceof expressionNode.ForValue) {
        // If we receive a value node, we can return a value node
        return new expressionNode.ForValue(
          pack<unknown>(argNode.value).every(val => !!val),
          common.value.types.Intrinsic.Boolean,
        )
      }
      type ArgFnNode =
        typeof argNode extends expressionNode.Node<infer T>
          ? fnNode.Node<T>
          : never
      if (argNode instanceof fnNode.Node) {
        // NOTE: TypeScript fails to infer type parameters correctly with
        // instanceof, so we have to help it along by using the `ArgFnNode`
        // helper type
        return new Node(argNode as ArgFnNode)
      }
      // We don't know how to handle other types of nodes
      throw new UnsupportedNodeError()
    },
    expressionsInArgs,
    typeChecker: isEvery,
  }
}
